/**
 * ResiBot / widget tenant id for ResiWeb embeds.
 *
 * ARCHITECTURE_PLAN.md: `data-tenant-id` must be the accommodation key in ResiBot MySQL
 * (e.g. studio_gocki), not a display name or raw email.
 *
 * Priority:
 * 1. REACT_APP_RESIBOT_TENANT_ID — per-deploy override (single-property build)
 * 2. REACT_APP_RESIBOT_DEFAULT_TENANT or "studio_gocki"
 *
 * We do not pass `username` verbatim as tenant (often an email); that breaks
 * GET /api/widget/config/{tenantId} when the DB row is keyed by property slug.
 */

// Domain → ResiBot tenant_id, for the properties ResiBot is actually enabled for.
// This repo's build is shared across every property site — one `npm run build`
// gets rsynced to all of them (see .github/workflows/main.yml) — so a build-time
// env var like REACT_APP_RESIBOT_TENANT_ID is identical on every site and can't
// tell them apart. The real per-site signal is the runtime `username`
// (window.location.host, resolved in AuthProvider.js), so that's checked first.
const TENANT_ID_BY_DOMAIN = {
  "studiogocki.com": "studio_gocki",
  "hausclarysse.com": "haus_clarysse",
  "activemontgoflat.com": "active_montgo_flat",
  "thecityghent.com": "the_city_ghent",
};

// Hard allowlist for whether the widget should render at all. This build is
// shared across every property site, and the per-property on/off switch
// (`showResiBot` in the accommodation form) isn't backed by a real DB column in
// production — the lookup always fails, and the caller defaults to "on" when it
// does. Without this, that failure would show the widget (with whichever
// tenant's data) on every site this repo deploys to, not just the ones ResiBot
// is actually set up for.
export function isResibotEnabledDomain(username) {
  const domain = (username || "").trim().toLowerCase();
  return Object.prototype.hasOwnProperty.call(TENANT_ID_BY_DOMAIN, domain);
}

export function getWidgetTenantId(username) {
  const domain = (username || "").trim().toLowerCase();
  if (TENANT_ID_BY_DOMAIN[domain]) return TENANT_ID_BY_DOMAIN[domain];

  // Per-deploy override — only meaningful for an actual single-property build,
  // not the shared multi-site build this repo normally produces.
  const explicit = process.env.REACT_APP_RESIBOT_TENANT_ID?.trim();
  if (explicit) return explicit.toLowerCase();

  const fallback =
    process.env.REACT_APP_RESIBOT_DEFAULT_TENANT?.trim().toLowerCase() ||
    "studio_gocki";

  if (!domain || domain === "default" || domain === "localhost") {
    return fallback;
  }

  const legacy =
    String(process.env.REACT_APP_RESIBOT_USE_USERNAME_AS_TENANT || "")
      .trim()
      .toLowerCase() === "true";
  if (legacy) {
    return (
      domain.replace(/[^a-z0-9_-]/g, "_").replace(/_+/g, "_").replace(/^_|_$/g, "") ||
      fallback
    );
  }

  return fallback;
}
